import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useToast } from '../../../../context/ToastContext';
import apiService from '../../../../services/apiService';
import { Button, MultiselectDropdown } from '../../../../components/ui';
import { FaSave, FaTimes } from 'react-icons/fa';

const breadcrumb = (
  <span>
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Home
    </span> &nbsp;/&nbsp; 
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Courses
    </span> &nbsp;/&nbsp; 
    <span className="text-gray-700">Add New</span>
  </span>
);

const AddCourse = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, formState: { errors, isSubmitting }, setValue, watch } = useForm({
    defaultValues: {
      name: '',
      uri: '',
      thumb: '',
      order: 0,
      status: true,
      eduStandards: [],
      metaTags: {
        keywords: '',
        ogDescription: '',
        ogImage: '',
        ogTitle: ''
      }
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [eduStandards, setEduStandards] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  const [selectedStandards, setSelectedStandards] = useState([]);

  // No need to fetch courses as this is the course add page

  // Handle education standards change
  const handleStandardsChange = (newValues) => {
    // Update both form fields when standards change
    setValue('eduStandards', newValues, { shouldValidate: true, shouldDirty: true });
    // Set first selected standard as eduStandard
    if (newValues.length > 0) {
      setValue('eduStandard', newValues[0], { shouldValidate: true, shouldDirty: true });
    } else {
      setValue('eduStandard', null, { shouldValidate: true, shouldDirty: true });
    }
    setSelectedStandards(newValues);
    
    console.log('Education standards updated:', {
      eduStandard: newValues[0] || null,
      eduStandards: newValues
    });
  };

  // Fetch education standards
  useEffect(() => {
    const fetchEduStandards = async () => {
      try {
        const data = await apiService.request('/i/ims/edustandard?limit=all&status=true&select=name');
        if (Array.isArray(data)) {
          setEduStandards(data);
        } else {
          console.error('Education standard data is not an array:', data);
          setEduStandards([]);
        }
      } catch (error) {
        console.error('Error fetching education standards:', error);
        setEduStandards([]);
      }
    };

    fetchEduStandards();
  }, []);

  const onSubmit = async (formData) => {
    try {
      setLoading(true);
      
      // Create a clean copy of form data
      const payload = { ...formData };
      
      // Process education standards
      const { eduStandards = [] } = formData;
      const hasStandards = Array.isArray(eduStandards) && eduStandards.length > 0;
      
      // Prepare education standards in the required format
      if (hasStandards) {
        // First standard is the primary one
        payload.eduStandard = eduStandards[0];
        // All standards go into eduStandards array
        payload.eduStandards = [...eduStandards];
      } else {
        // If no standards selected, ensure both fields are cleared
        payload.eduStandard = null;
        payload.eduStandards = [];
      }
      
      // Ensure metaTags exists with all required fields
      payload.metaTags = {
        keywords: payload.metaTags?.keywords || '',
        ogDescription: payload.metaTags?.ogDescription || '',
        ogImage: payload.metaTags?.ogImage || '',
        ogTitle: payload.metaTags?.ogTitle || ''
      };
      
      // Convert status to boolean if it's a string
      if (typeof payload.status === 'string') {
        payload.status = payload.status === 'true';
      }
      
      // Convert order to number
      payload.order = Number(payload.order) || 0;
      
      console.log('Submitting course data:', payload);
      
      await apiService.request('/i/ims/course', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      showSuccess('Course created successfully');
      navigate('/course-programs/courses');
    } catch (error) {
      console.error('Error creating course:', error);
      showError('Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="border-b border-gray-200">
        <div className="p-6">
          <nav className="text-xs text-gray-500 mb-4" aria-label="Breadcrumb">
            {breadcrumb}
          </nav>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Add Course</h1>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveTab('basic')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'basic'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Basic Details
              </button>
              <button
                onClick={() => setActiveTab('meta')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'meta'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Meta Tags
              </button>
            </nav>
          </div>
        </div>
        
        <div className="p-6">
          <form onSubmit={handleSubmit(onSubmit)}>
            {activeTab === 'basic' && (
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="name">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('name', { required: 'Name is required' })}
                    placeholder="e.g., Course 1"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="uri">
                    URI <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="uri"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('uri', { required: 'URI is required' })}
                    placeholder="e.g., course-1"
                  />
                  {errors.uri && (
                    <p className="text-red-500 text-sm mt-1">{errors.uri.message}</p>
                  )}
                </div>

                <MultiselectDropdown
                  label="Education Standards"
                  options={eduStandards}
                  selectedValues={selectedStandards}
                  onChange={handleStandardsChange}
                  placeholder="Select education standards"
                  disabled={eduStandards.length === 0}
                />

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="thumb">
                    Thumb
                  </label>
                  <input
                    id="thumb"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('thumb')}
                    placeholder="Enter thumbnail URL"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="order">
                    Order
                  </label>
                  <input
                    id="order"
                    type="number"
                    className="w-full border rounded px-3 py-2"
                    {...register('order')}
                    placeholder="e.g., 1"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <div className="flex space-x-4">
                    <div className="flex items-center">
                      <input
                        id="statusActive"
                        type="radio"
                        name="status"
                        className="mr-1"
                        checked={watch('status') === true || watch('status') === 'true'}
                        onChange={() => {
                          setValue('status', true);
                        }}
                      />
                      <label htmlFor="statusActive" className="ml-1">Active</label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        id="statusInactive"
                        type="radio"
                        name="status"
                        className="mr-1"
                        checked={watch('status') === false || watch('status') === 'false'}
                        onChange={() => {
                          setValue('status', false);
                        }}
                      />
                      <label htmlFor="statusInactive" className="ml-1">Inactive</label>
                    </div>
                  </div>
                  {errors.status && (
                    <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
                  )}
                </div>
              </div>
            )}
            
            {activeTab === 'meta' && (
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogTitle">
                    OG Title
                  </label>
                  <input
                    id="metaTags.ogTitle"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('metaTags.ogTitle')}
                    placeholder="e.g., My Awesome Course"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.keywords">
                    Keywords
                  </label>
                  <input
                    id="metaTags.keywords"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    {...register('metaTags.keywords')}
                    placeholder="e.g., education, course"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogDescription">
                    OG Description
                  </label>
                  <textarea
                    id="metaTags.ogDescription"
                    className="w-full border rounded px-3 py-2"
                    rows="2"
                    {...register('metaTags.ogDescription')}
                    placeholder="Enter meta description"
                  ></textarea>
                </div>

                <div className="mb-4">
                  <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogImage">
                    OG Image
                  </label>
                  <input
                    id="metaTags.ogImage"
                    type="text"
                    className="w-full border rounded px-3 py-2"
                    placeholder="Image URL"
                    {...register('metaTags.ogImage')}
                  />
                </div>
              </div>
            )}
            
            <div className="flex justify-between mt-6">
              <Button
                type="button"
                onClick={() => navigate('/course-programs/courses')}
                variant="outline"
                size="md"
                className="border-gray-300 text-gray-700"
                leftIcon={<FaTimes />}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={isSubmitting || loading}
                leftIcon={<FaSave />}
                className={`${(isSubmitting || loading) ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting || loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCourse;
